/**
 * RenameDialog
 * Renames a SOP or Knowledge source. Shared by the "Rename" row action on both the
 * SOP's and Knowledge tabs — mirrors AddToCategoryDialog's structure.
 */

import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface RenameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentName: string;
  isSubmitting?: boolean;
  onSubmit: (name: string) => void | Promise<void>;
}

export function RenameDialog({ open, onOpenChange, currentName, isSubmitting, onSubmit }: RenameDialogProps) {
  const [name, setName] = useState(currentName);

  useEffect(() => {
    if (open) setName(currentName);
  }, [open, currentName]);

  const canSubmit = name.trim().length > 0 && name.trim() !== currentName.trim();

  const handleRename = async () => {
    if (!canSubmit) return;
    await onSubmit(name.trim());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='w-md p-4'>
        <DialogHeader>
          <DialogTitle className='text-sm font-semibold'>Rename</DialogTitle>
        </DialogHeader>

        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder='Enter a name'
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') void handleRename();
          }}
        />

        <DialogFooter className='mt-2'>
          <Button type='button' variant='outline' size='sm' className='text-xs' onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type='button' size='sm' className='text-xs' onClick={handleRename} disabled={!canSubmit || isSubmitting}>
            {isSubmitting ? 'Renaming…' : 'Rename'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
